// src/components/suiviEnseignants/formatSuivi.js
// ----------------------------------------------------------------------------
// Mises en forme du suivi des enseignants et des avis (administration).
//
// Rien ici ne DÉCIDE à partir de l'horloge du poste : la semaine en cours, son
// lundi, l'appel « manquant » ou « à venir », le programme « en retard » sont
// calculés par le serveur, en SQL, avec SA date du jour. Ces fonctions ne font
// qu'écrire en français ce qu'il a renvoyé.
//
// Le serveur envoie ses dates en chaînes (`AAAA-MM-JJ`, `AAAA-MM-JJ HH:MM:SS`).
// Elles sont relues SANS passer par le fuseau du poste : posées en UTC, écrites
// en UTC. Un `new Date('2026-11-16')` relu à l'heure locale afficherait le 15
// à l'ouest de Greenwich. Seul un instant ISO complet (`…Z`), si le pilote en
// laissait passer un, est relu à l'heure du poste : c'est alors un instant.
// ----------------------------------------------------------------------------

const MOTIF_DATE = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/;
const MOTIF_INSTANT = /(Z|[+-]\d{2}:?\d{2})$/;

export const EST_DATE = /^\d{4}-\d{2}-\d{2}$/;

const enFrancais = (options, utc = true) => new Intl.DateTimeFormat(
  'fr-FR',
  utc ? { timeZone: 'UTC', ...options } : options
);

/**
 * `{ d, utc }` : la date à écrire et le fuseau dans lequel l'écrire. `null`
 * pour une valeur vide ou illisible.
 */
function lire(valeur) {
  const texte = String(valeur || '').trim();
  if (!texte) return null;
  if (MOTIF_INSTANT.test(texte)) {
    const d = new Date(texte);
    return Number.isNaN(d.getTime()) ? null : { d, utc: false, heure: true };
  }
  const m = MOTIF_DATE.exec(texte);
  if (!m) return null;
  const d = new Date(Date.UTC(
    Number(m[1]), Number(m[2]) - 1, Number(m[3]),
    Number(m[4] || 0), Number(m[5] || 0)
  ));
  return Number.isNaN(d.getTime()) ? null : { d, utc: true, heure: m[4] !== undefined };
}

const ecrire = (valeur, options) => {
  const lu = lire(valeur);
  return lu ? enFrancais(options, lu.utc).format(lu.d) : '';
};

const majuscule = (texte) => (texte ? texte.charAt(0).toUpperCase() + texte.slice(1) : texte);

/** « 17 nov. » — la date d'un avis dans une liste. */
export const dateCourte = (valeur) => ecrire(valeur, { day: 'numeric', month: 'short' });

/** « 17 novembre 2026 ». */
export const dateLongue = (valeur) => ecrire(valeur, { day: 'numeric', month: 'long', year: 'numeric' });

/** « 17 nov. 2026 à 07:12 » ; la date seule si la valeur n'a pas d'heure. */
export function dateHeure(valeur) {
  const lu = lire(valeur);
  if (!lu) return '';
  const jour = enFrancais({ day: 'numeric', month: 'short', year: 'numeric' }, lu.utc).format(lu.d);
  if (!lu.heure) return jour;
  return `${jour} à ${enFrancais({ hour: '2-digit', minute: '2-digit' }, lu.utc).format(lu.d)}`;
}

/** « Lun. 16 » — l'en-tête d'une colonne de la grille des appels. */
export function jourCourt(valeur) {
  const lu = lire(valeur);
  if (!lu) return '';
  const nom = enFrancais({ weekday: 'short' }, lu.utc).format(lu.d);
  const numero = enFrancais({ day: 'numeric' }, lu.utc).format(lu.d);
  return `${majuscule(nom)} ${numero}`;
}

/** `2026-11-16` + 7 → `2026-11-23`, en UTC (sert seulement à demander la semaine voisine). */
export function decalerJours(chaine, jours) {
  const lu = EST_DATE.test(String(chaine || '')) ? lire(chaine) : null;
  if (!lu) return null;
  const d = new Date(lu.d.getTime());
  d.setUTCDate(d.getUTCDate() + jours);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

/**
 * « du 16 au 21 novembre », « du 30 novembre au 5 décembre »,
 * « du 29 décembre 2025 au 3 janvier 2026 » — la semaine que le serveur a choisie.
 */
export function periode(debut, fin) {
  const a = lire(debut);
  const b = lire(fin);
  if (!a || !b) return '';
  const memeAnnee = a.d.getUTCFullYear() === b.d.getUTCFullYear();
  const memeMois = memeAnnee && a.d.getUTCMonth() === b.d.getUTCMonth();
  const premier = memeMois
    ? enFrancais({ day: 'numeric' }).format(a.d)
    : enFrancais(memeAnnee ? { day: 'numeric', month: 'long' } : { day: 'numeric', month: 'long', year: 'numeric' }).format(a.d);
  const dernier = enFrancais(memeAnnee ? { day: 'numeric', month: 'long' } : { day: 'numeric', month: 'long', year: 'numeric' }).format(b.d);
  return `du ${premier} au ${dernier}`;
}

/** « 1 avis », « 3 cours » : accord du seul nom, au pluriel régulier par défaut. */
export function pluriel(nombre, singulier, plurielForme = `${singulier}s`) {
  const n = Number(nombre || 0);
  return `${n} ${n > 1 ? plurielForme : singulier}`;
}

/** 0 à 100, entier, arrondi vers le bas comme le serveur : « 100 % » seulement quand tout est vu. */
export function pourcentage(valeur) {
  const n = Number(valeur);
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, Math.floor(n)));
}

/** « 18 % », avec l'espace insécable de la typographie française. */
export const enPourcentage = (valeur) => `${pourcentage(valeur)} %`;

/** « Arnaud MOUKOKO » — un membre du personnel, prénom puis nom. */
export const nomPersonne = (p) => [p?.prenom, p?.nom].filter(Boolean).join(' ').trim();

/** « MBEMBA Rodrigue » — un élève, nom puis prénom, comme dans les listes de classe. */
export const nomEleve = (e) => [e?.nom, e?.prenom].filter(Boolean).join(' ').trim();

/** « AM » — l'avatar quand il n'y a pas de photo. */
export function initiales(p) {
  const lettres = [p?.prenom, p?.nom]
    .filter(Boolean)
    .map((partie) => String(partie).trim().charAt(0).toUpperCase())
    .join('');
  return lettres || '?';
}

/**
 * Un nom venu du serveur sous forme de chaîne ou d'objet (`{ id, nom }`) :
 * le contrat donne une chaîne, l'objet est toléré pour ne pas afficher
 * « [object Object] » si la forme change.
 */
export const libelleDe = (valeur) => {
  if (!valeur) return '';
  if (typeof valeur === 'string') return valeur;
  return valeur.nom || valeur.libelle || '';
};

/** Comparaison de recherche sans accents ni casse : « eleve » trouve « Élève ». */
export const sansAccents = (texte) => String(texte || '')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .toLowerCase();

/**
 * MySQL rend ses booléens calculés en 0/1, parfois en chaîne : un
 * `{x && …}` afficherait « 0 » au milieu de l'écran.
 */
export const estVrai = (valeur) => valeur === true || Number(valeur) === 1;

/** Un nombre venu du serveur, `true`/`false` compris (un « en retard » peut être l'un ou l'autre). */
export const nombre = (valeur) => {
  if (valeur === true) return 1;
  const n = Number(valeur);
  return Number.isFinite(n) ? n : 0;
};
